import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { ContentSectionBlocksParams } from "~/landing-pages/components/ContentSections/content-section-shared-types";
import {
  statDescriptionStyles,
  statValueStyles,
} from "~/landing-pages/components/Stats/shared-stat-styles";
import { LandingPageStat } from "~/landing-pages/landing-page-types";
import { primaryTextColorStyle } from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

export type ContentSectionStatsBlockProps = {
  stats: LandingPageStat[];
  appearance: "brand" | "dark" | "light";
};

const ContentSectionStatsBlock = ({
  stats,
  appearance,
}: ContentSectionStatsBlockProps): JSX.Element => (
  <section>
    <dl className="grid grid-cols-1 gap-x-8 gap-y-16 text-center sm:grid-cols-2 lg:grid-cols-3">
      {stats.map((stat) => (
        <div key={`${stat.name}-${stat.value}`}>
          <Headline
            as="dd"
            level={1}
            mdLevel={0}
            className={statValueStyles({ appearance })}
          >
            {stat.value}
          </Headline>
          <Body as="dt" className={statDescriptionStyles({ appearance })}>
            {stat.name}
          </Body>
        </div>
      ))}
    </dl>
  </section>
);

export const ContentSectionBlocks = ({
  contentBlocks,
  appearance,
}: ContentSectionBlocksParams): JSX.Element => (
  <>
    {contentBlocks.map((block, idx) => {
      if (block.acfFcLayout === "paragraph") {
        return (
          <Body
            key={`paragraph ${idx}`}
            size={block.display === "large" ? "large" : "default"}
            className={primaryTextColorStyle({
              appearance,
              className: "mt-6",
            })}
            as="p"
          >
            {block.paragraph}
          </Body>
        );
      }
      if (block.acfFcLayout === "bullets") {
        return (
          <ul className="mt-8 space-y-8 text-gray-600" key={`list ${idx}`}>
            {block.bullets.map((bullet, index) => (
              <li
                className={primaryTextColorStyle({
                  appearance,
                  className: "gap-x-3",
                })}
                key={`list-item ${index}`}
              >
                <Body as="span" bold size="large" className="mr-2">
                  {bullet.title}
                </Body>

                <Body as="span">
                  <BuildHTML
                    html={bullet.description}
                    tagToClassNameMap={tagToClassNameMap}
                  />
                </Body>
              </li>
            ))}
          </ul>
        );
      }
      if (block.acfFcLayout === "header") {
        return (
          <Headline
            as={"h3"}
            level={3}
            key={`header-${idx}`}
            className={primaryTextColorStyle({ appearance })}
          >
            {block.header}
          </Headline>
        );
      }
      if (block.acfFcLayout === "stats") {
        return (
          <section className="mt-16" key={`stats-${idx}`}>
            <ContentSectionStatsBlock
              appearance={appearance}
              stats={block.stats}
            />
          </section>
        );
      }
      return null;
    })}
  </>
);
